import type { BoxProps, PaperProps } from '@mui/material';
import { Box, Paper, styled, Typography } from '@mui/material';
import CoverageCheckbox from '../../customizeQuote/components/CoverageCheckbox';
import CoverageLineItem from './CoverageLineItem';
import CoverageGroupHeader from './CoverageGroupHeader';
import type { ProductType } from '../../../../../../types';
import { getRegionByProduct } from '../../../../../../i18n/utils';
import type { GetPresetsResponse } from '../../../../../../api/rater.api';
import { useAllow } from '../../../../../../components/hooks/useAllow';

export interface CoverageDetailsProps {
  coveragesByGrouping: GetPresetsResponse['coverageGroups'];
  productType: ProductType;
  boxProps?: BoxProps;
  containerProps?: PaperProps;
}

const CoverageDetails = ({
  coveragesByGrouping,
  productType,
  boxProps = {},
  containerProps = {},
}: CoverageDetailsProps) => {
  const language = getRegionByProduct(productType);
  const canUWAccounts = useAllow(['accounts:uw']);

  if (!coveragesByGrouping) {
    return null;
  }

  return (
    <Box {...boxProps}>
      <Typography variant="h6" style={{ marginBottom: '0.5rem' }}>
        Coverage Details
      </Typography>
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}
      >
        <CoverageCheckbox.Mandatory />
        <CoverageCheckbox.Selected disabled />
        <CoverageCheckbox.Available disabled />
        <CoverageCheckbox.NotAvailable />
      </div>

      {Object.keys(coveragesByGrouping).map((coverageGroup) => {
        return (
          <CoverageContainer
            elevation={5}
            className="contrast-text"
            key={coverageGroup}
            {...containerProps}
          >
            <CoverageGroupHeader>
              <Typography style={{ textTransform: 'capitalize' }}>
                {coverageGroup.split('_').join(' ').toLowerCase()}
              </Typography>
            </CoverageGroupHeader>
            {coveragesByGrouping?.[coverageGroup]?.map((coverage, index) => {
              if (!canUWAccounts && coverage.preset.isHidden) {
                return null;
              }

              return (
                <CoverageLineItem
                  key={index}
                  isOdd={index % 2 !== 0}
                  language={language}
                  {...coverage.preset}
                />
              );
            })}
          </CoverageContainer>
        );
      })}
    </Box>
  );
};

const CoverageContainer = styled(Paper)({
  marginBottom: '2rem',
  borderRadius: 4,
});

export default CoverageDetails;
